import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, Download, Loader2, FileJson, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import RecordDetailModal from "./RecordDetailModal";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

const poolColor = (pool) => {
  if (pool === "Pool A") return "secondary";
  if (pool === "Pool B") return "outline";
  return "secondary";
};

export default function RecordBrowser({ filename, token, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState("all");
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  const load = useCallback(async (gradeFilter) => {
    setLoading(true);
    try {
      const qs = gradeFilter && gradeFilter !== "all" ? `?grade=${encodeURIComponent(gradeFilter)}` : "";
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/registry/${encodeURIComponent(filename)}/records${qs}`, {
        headers: authHdr(token),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Failed to load records"); return; }
      setData(json);
    } catch { toast.error("Network error loading records"); }
    finally { setLoading(false); }
  }, [filename, token]);

  useEffect(() => { load(grade); }, [load, grade]);

  const handleDownload = () => {
    const url = `${API}/api/admin/cr/dataset-ops/registry/${encodeURIComponent(filename)}/download`;
    fetch(url, { headers: authHdr(token) })
      .then(async (res) => {
        if (!res.ok) { toast.error("Download failed"); return; }
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error("Network error during download"));
  };

  return (
    <Card className="p-5 border border-slate-200 shadow-none" data-testid="record-browser">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="record-browser-back-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
          </Button>
          <h3 className="font-semibold text-slate-800 flex items-center gap-1.5">
            <FileJson className="w-4 h-4 text-slate-400" /> {filename}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="w-40" data-testid="record-browser-grade-filter">
              <SelectValue placeholder="All grades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All grades</SelectItem>
              {(data?.grades || []).map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="record-browser-download-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-3" data-testid="record-browser-total-count">
            {data?.total_records ?? 0} record{data?.total_records === 1 ? "" : "s"}
            {grade !== "all" ? ` · Grade: ${grade}` : ""}
          </p>
          <Accordion type="multiple" className="space-y-1" data-testid="record-browser-subjects">
            {(data?.subjects || []).map((s) => (
              <AccordionItem key={s.subject} value={s.subject} className="border border-slate-200 rounded-lg px-3" data-testid={`subject-group-${s.subject}`}>
                <AccordionTrigger className="hover:no-underline">
                  <span className="text-sm font-medium text-slate-700">{s.subject}</span>
                  <Badge variant="outline" className="ml-2 mr-auto">{s.sub_topics.reduce((n, g) => n + g.records.length, 0)} records</Badge>
                </AccordionTrigger>
                <AccordionContent>
                  <Accordion type="multiple" className="space-y-1">
                    {s.sub_topics.map((grp) => (
                      <AccordionItem key={grp.sub_topic} value={grp.sub_topic} className="border border-slate-100 rounded-lg px-3 bg-slate-50" data-testid={`subtopic-group-${grp.sub_topic}`}>
                        <AccordionTrigger className="hover:no-underline py-2.5">
                          <span className="text-xs font-medium text-slate-600 text-left">{grp.sub_topic}</span>
                          <Badge variant="secondary" className="ml-2 mr-auto text-[10px]">{grp.records.length}</Badge>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-1">
                            {grp.records.map((r) => (
                              <div
                                key={r.record_id}
                                role="button"
                                onClick={() => setSelectedRecordId(r.record_id)}
                                className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-100 rounded-md px-2.5 py-1.5 cursor-pointer hover:border-sky-300 hover:bg-sky-50/50 transition-colors"
                                data-testid={`record-row-${r.record_id}`}
                              >
                                <span className="text-slate-700 font-mono">{r.record_id}</span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {r.is_trap && (
                                    <Badge variant="destructive" className="text-[10px] flex items-center gap-0.5" data-testid={`record-trap-${r.record_id}`}>
                                      <AlertTriangle className="w-2.5 h-2.5" /> Trap
                                    </Badge>
                                  )}
                                  {r.diagnostic_signal && r.diagnostic_signal !== "normal" && (
                                    <Badge variant="outline" className="text-[10px]" data-testid={`record-diagnostic-${r.record_id}`}>{r.diagnostic_signal}</Badge>
                                  )}
                                  {r.difficulty && <Badge variant="outline" className="text-[10px]">{r.difficulty}</Badge>}
                                  {r.question_pool && <Badge variant={poolColor(r.question_pool)} className="text-[10px]">{r.question_pool}</Badge>}
                                  <Badge variant="outline" className="text-[10px]">{r.dataset_type}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {(data?.subjects || []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">No records match this filter.</p>
          )}
        </>
      )}

      {selectedRecordId && (
        <RecordDetailModal
          recordId={selectedRecordId}
          filename={filename}
          token={token}
          onClose={() => setSelectedRecordId(null)}
        />
      )}
    </Card>
  );
}
